import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HexAlphaColorPicker } from 'react-colorful';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import styled from 'styled-components';

import { ErrorMessage } from '../../Typhography/ErrorMessage';

// #AARRGGBB (Flutter/Firestore) <-> #RRGGBBAA (react-colorful)
function argbToRgba(hex: string): string {
  const c = hex.replace('#', '');
  if (c.length !== 8) return hex;
  return `#${c.slice(2)}${c.slice(0, 2)}`;
}

function rgbaToArgb(hex: string): string {
  const c = hex.replace('#', '');
  // react-colorful drops the AA suffix when alpha is FF (fully opaque)
  if (c.length === 6) return `#FF${c}`.toUpperCase();
  if (c.length !== 8) return hex;
  return `#${c.slice(6)}${c.slice(0, 6)}`.toUpperCase();
}

const HEX8_REGEX = /^#?[0-9a-fA-F]{8}$/;

function normalizeArgb(value: string): string {
  const c = value.trim().replace('#', '');
  return `#${c}`.toUpperCase();
}

const Wrapper = styled.div<{ margin?: string }>`
  position: relative;
  margin: ${({ margin }) => margin || '0 0 20px 0'};
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #c4c4c4;
  border-radius: 4px;
  padding: 4px 8px;
  background: #fff;
`;

const Swatch = styled.div<{ color: string }>`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: 1px solid #ccc;
  background: ${({ color }) => color};
  cursor: pointer;
  flex-shrink: 0;
`;

const HexInput = styled.input`
  border: none;
  outline: none;
  font-family: monospace;
  font-size: 13px;
  width: 110px;
  text-transform: uppercase;
  background: transparent;
`;

const FieldLabel = styled.label`
  font-size: 11px;
  color: #888;
  font-weight: 600;
  letter-spacing: 0.4px;
  min-width: 0;
  flex: 1;
`;

const PopoverWrapper = styled.div`
  position: absolute;
  top: 110%;
  left: 0;
  z-index: 100;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ClearBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  color: #aaa;
  font-size: 14px;
  line-height: 1;
  padding: 2px 4px;
  flex-shrink: 0;
  &:hover {
    color: #e55;
  }
`;

interface ColorFieldInnerProps {
  value: string;
  onChange: (v: string) => void;
  label?: string;
  disabled?: boolean;
  clearable?: boolean;
  error?: { message?: string };
}

const ColorFieldInner: React.FC<ColorFieldInnerProps> = ({
  value,
  onChange,
  label,
  disabled,
  clearable,
  error,
}) => {
  const [open, setOpen] = useState(false);
  const [hexInput, setHexInput] = useState(value || '');
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setHexInput(value || '');
  }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handlePickerChange = useCallback(
    (rgba: string) => {
      // react-colorful gives #RRGGBBAA, convert to #AARRGGBB
      const argb = rgbaToArgb(rgba);
      setHexInput(argb);
      onChange(argb);
    },
    [onChange],
  );

  const handleHexInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setHexInput(raw);
      if (HEX8_REGEX.test(raw)) {
        onChange(normalizeArgb(raw));
      }
    },
    [onChange],
  );

  const handleHexInputBlur = useCallback(() => {
    if (HEX8_REGEX.test(hexInput)) {
      const normalized = normalizeArgb(hexInput);
      setHexInput(normalized);
      onChange(normalized);
    } else {
      setHexInput(value || '');
    }
  }, [hexInput, value, onChange]);

  // Convert stored #AARRGGBB to CSS rgba for the swatch display
  const swatchCss = (() => {
    const c = (value || '#000000ff').replace('#', '');
    if (c.length === 8) {
      const a = parseInt(c.slice(0, 2), 16) / 255;
      const r = parseInt(c.slice(2, 4), 16);
      const g = parseInt(c.slice(4, 6), 16);
      const b = parseInt(c.slice(6, 8), 16);
      return `rgba(${r},${g},${b},${a.toFixed(2)})`;
    }
    return '#000';
  })();

  // Convert #AARRGGBB → #RRGGBBAA for react-colorful
  const pickerValue = HEX8_REGEX.test(value || '') ? argbToRgba(value) : '#000000ff';

  return (
    <Wrapper>
      <FieldRow>
        <Swatch
          color={swatchCss}
          onClick={() => !disabled && setOpen((o) => !o)}
          title="Pick color"
        />
        <HexInput
          value={hexInput}
          onChange={handleHexInputChange}
          onBlur={handleHexInputBlur}
          disabled={disabled}
          placeholder="#AARRGGBB"
          spellCheck={false}
        />
        {label && <FieldLabel>{label}</FieldLabel>}
        <div style={{ flex: 1 }} />
        {clearable && value && !disabled && (
          <ClearBtn
            type="button"
            title="Clear color"
            onClick={() => {
              setHexInput('');
              setOpen(false);
              onChange('');
            }}
          >
            ✕
          </ClearBtn>
        )}
      </FieldRow>
      {open && !disabled && (
        <PopoverWrapper ref={popoverRef}>
          <HexAlphaColorPicker color={pickerValue} onChange={handlePickerChange} />
          <div style={{ fontSize: 11, color: '#888', marginTop: 6, textAlign: 'center' }}>
            Format stored: #AARRGGBB
          </div>
        </PopoverWrapper>
      )}
      {error?.message && <ErrorMessage>{error.message}</ErrorMessage>}
    </Wrapper>
  );
};

interface ColorFieldProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues, any>;
  label?: string;
  disabled?: boolean;
  clearable?: boolean;
  defaultValue?: string;
  error?: { message?: string };
  margin?: string;
}

function ColorField<TFieldValues extends FieldValues>({
  name,
  control,
  label,
  disabled,
  clearable,
  defaultValue = '#FF000000',
  error,
  margin,
}: ColorFieldProps<TFieldValues>) {
  return (
    <Wrapper margin={margin}>
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue as any}
        render={({ field: { value, onChange } }) => (
          <ColorFieldInner
            value={value}
            onChange={onChange}
            label={label}
            disabled={disabled}
            clearable={clearable}
            error={error}
          />
        )}
      />
    </Wrapper>
  );
}

export default ColorField;
